"use client";

import React, { FC, memo } from "react";
import { cn } from "utils";
import { AssetImage } from "@ospweb/sdk-ui";
import { AssetsItemProps } from "./types";
import CheckBox from "@/src/components/CheckBox";
import { numberStatus } from "@/src/utils";

/**
 * AssetsItem 单个资产项组件
 * 显示单个资产的详细信息，包括复选框、图标、代码、名称、价格和涨跌幅
 *
 * @param props AssetsItemProps
 * @returns React.ReactElement
 */
export const AssetsItem: FC<AssetsItemProps> = memo(
  ({ asset, isSelected, onSelectionChange, className, type = "normal" }) => {
    /**
     * 处理复选框点击事件
     */
    const handleCheckboxClick = () => {
      onSelectionChange(asset.id, !isSelected);
    };

    /**
     * 解析涨跌幅字符串，提取数值用于判断颜色
     */

    const { isPositive, isNegative, isZero } = numberStatus(
      Number(asset.change.replace("%", "")),
    );

    return (
      <div
        className={cn(
          "flex items-center gap-3 border-b border-border_white_transparent10 last:border-b-0",
          type === "modal" ? "py-3" : "md:py-6 py-3",
          className,
        )}
      >
        {/* 复选框 */}
        <CheckBox
          data-openreplay-label="AddAssets · AssetsItem · CheckBox"
          checked={isSelected}
          onClick={handleCheckboxClick}
          classNames="w-4 h-4"
          iconSize={16}
        />

        {/* 资产图标 */}
        <div className="flex-shrink-0 border-2 border-border_solid rounded-full">
          <AssetImage
            src={asset.iconUrl}
            alt={asset.name}
            width={type === "modal" ? 32 : 40}
            height={type === "modal" ? 32 : 40}
            className="rounded-full"
          />
        </div>

        {/* 资产信息 */}
        <div className="flex-1 flex flex-col gap-0.5">
          {/* 代码 */}
          <span
            className={cn(
              " text-content_primary",
              type === "modal" ? "body_medium_m" : "body_medium_l",
            )}
          >
            {asset.ticker}
          </span>
          {/* 名称 */}
          <span
            className={cn(
              "text-content_secondary",
              type === "modal" ? "body_s" : "body_m",
            )}
          >
            {asset.name}
          </span>
        </div>

        {/* 价格和涨跌幅 */}
        <div className="flex flex-col items-end gap-0.5">
          {/* 价格 */}
          <span
            className={cn(
              "text-content_primary",
              type === "modal" ? "body_medium_m" : "body_medium_l",
            )}
          >
            {asset.value}
          </span>
          {/* 涨跌幅 */}
          <span
            className={cn(
              type === "modal" ? "body_medium_s" : "body_medium_m",
              {
                "text-content_accent1_primary": isPositive && !isZero,
                "text-content_accent2_primary": isNegative && !isZero,
                "text-content_secondary": isZero,
              },
            )}
          >
            {`${asset.change}`}
          </span>
        </div>
      </div>
    );
  },
);

AssetsItem.displayName = "AssetsItem";
